// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  AccessTime as ClockIcon,
  Error as ErrorIcon,
  Description as ClaimIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../../common/CustomToasts';

const maintainClaimOrder = /* GraphQL */ `
  mutation MaintainClaimOrder($request: AWSJSON!) {
    maintainClaimOrder(request: $request)
  }
`;

interface ClaimOrder {
  id: string;
  dealID: string;
  orgID: string;
  userID: string;
  dealName?: string;
  dealAddress?: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  pdfUrl?: string;
  orderedAt: string;
  readyAt?: string;
}

interface DealClaimsProps {
  dealID: string;
  dealInfo?: any;
  pollTick?: number;
}

function parseGraphQLResponse(raw: any): any {
  let parsed = raw;
  while (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }
  return parsed;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'READY':
      return '#4CAF50';
    case 'PROCESSING':
      return '#FF9800';
    case 'FAILED':
      return '#F44336';
    case 'PENDING':
    default:
      return '#9E9E9E';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'READY':
      return 'Ready to View';
    case 'PROCESSING':
      return 'Processing...';
    case 'FAILED':
      return 'Failed';
    case 'PENDING':
      return 'Pending';
    default:
      return status;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'READY':
      return <CheckIcon sx={{ fontSize: 16 }} />;
    case 'PROCESSING':
      return <ClockIcon sx={{ fontSize: 16 }} />;
    case 'FAILED':
      return <ErrorIcon sx={{ fontSize: 16 }} />;
    default:
      return <ClockIcon sx={{ fontSize: 16 }} />;
  }
};

const DealClaims: React.FC<DealClaimsProps> = ({
  dealID,
  dealInfo,
  pollTick,
}) => {
  const { theme } = useTheme();
  const [orders, setOrders] = useState<ClaimOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTickOnMountRef = React.useRef(pollTick);

  const fetchOrders = useCallback(async () => {
    if (!dealID) {
      setError('No deal ID provided');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request = JSON.stringify({
        operation: 'getClaimOrdersByDeal',
        dealID,
      });

      const result: any = await API.graphql(
        graphqlOperation(maintainClaimOrder, { request })
      );

      const parsed = parseGraphQLResponse(result?.data?.maintainClaimOrder);

      if (parsed?.success && parsed.data?.claimOrders) {
        setOrders(parsed.data.claimOrders);
      } else if (parsed?.success) {
        setOrders([]);
      } else {
        throw new Error(parsed?.error || 'Failed to fetch claim orders');
      }
    } catch (err: any) {
      console.error('Error fetching claim orders:', err);
      setError(err.message || 'Failed to load claim orders');
    } finally {
      setIsLoading(false);
    }
  }, [dealID]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (pollTick !== pollTickOnMountRef.current && pollTick && pollTick > 0) {
      fetchOrders();
    }
  }, [pollTick, fetchOrders]);

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      const request = JSON.stringify({
        operation: 'createClaimOrder',
        dealID,
        dealName: dealInfo?.name || dealInfo?.customerName || '',
        dealAddress:
          dealInfo?.address ||
          dealInfo?.streetAddress ||
          dealInfo?.fullAddress ||
          '',
      });

      const result: any = await API.graphql(
        graphqlOperation(maintainClaimOrder, { request })
      );

      const parsed = parseGraphQLResponse(result?.data?.maintainClaimOrder);

      if (parsed?.success && parsed.data?.claimOrder) {
        const newOrder = parsed.data.claimOrder;
        setOrders((prev) => [newOrder, ...prev]);
        GamifyToast.success('Claim submitted successfully');
      } else {
        throw new Error(parsed?.error || 'Failed to submit claim');
      }
    } catch (err: any) {
      console.error('Error submitting claim:', err);
      GamifyToast.error(
        err.message || 'Failed to submit claim. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewOrder = (order: ClaimOrder) => {
    if (order.status === 'READY' && order.pdfUrl) {
      window.open(order.pdfUrl, '_blank');
    } else if (order.status === 'PROCESSING') {
      GamifyToast.info(
        'Your claim is being prepared. Please check back shortly.'
      );
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 5,
        }}
      >
        <CircularProgress sx={{ color: theme.primary }} />
        <Typography sx={{ mt: 2, color: theme.textLight, fontSize: 14 }}>
          Loading claims...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 5,
        }}
      >
        <ErrorIcon sx={{ fontSize: 48, color: '#F44336', mb: 1 }} />
        <Typography sx={{ color: theme.text, fontSize: 14, mb: 1 }}>
          Error loading claims
        </Typography>
        <Typography sx={{ color: theme.textLight, fontSize: 13, mb: 2 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={fetchOrders}
          sx={{
            backgroundColor: `var(--fi-button, ${theme.primary})`,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: `var(--fi-button, ${theme.primary})`,
              opacity: 0.9,
            },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header with submit button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
          Claims
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setShowConfirmModal(true)}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <ClaimIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            backgroundColor: `var(--fi-button, ${theme.primary})`,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: `var(--fi-button, ${theme.primary})`,
              opacity: 0.9,
            },
          }}
        >
          Submit Claim
        </Button>
      </Box>

      {orders.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 5,
          }}
        >
          <ClaimIcon sx={{ fontSize: 48, color: theme.textLight, mb: 2 }} />
          <Typography sx={{ color: theme.textLight, fontSize: 14, mb: 1 }}>
            No claims yet
          </Typography>
          <Typography
            sx={{ color: theme.textLight, fontSize: 12, textAlign: 'center' }}
          >
            Claims are sent to our claim processing professional.
          </Typography>
        </Box>
      ) : (
        <Box>
          {orders.map((order, index) => (
            <Box
              key={order.id || index}
              onClick={() => handleViewOrder(order)}
              sx={{
                backgroundColor: theme.background2,
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                borderLeft: `4px solid ${getStatusColor(order.status)}`,
                cursor: order.status === 'READY' ? 'pointer' : 'default',
                '&:hover': order.status === 'READY' ? { opacity: 0.85 } : {},
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 600, color: theme.text }}
                  >
                    Insurance Claim
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}
                  >
                    Ordered {formatDate(order.orderedAt)}
                  </Typography>
                </Box>
                <Chip
                  icon={getStatusIcon(order.status)}
                  label={getStatusLabel(order.status)}
                  size="small"
                  sx={{
                    backgroundColor: `${getStatusColor(order.status)}20`,
                    color: getStatusColor(order.status),
                    fontWeight: 600,
                    fontSize: 11,
                    '& .MuiChip-icon': {
                      color: getStatusColor(order.status),
                    },
                  }}
                />
              </Box>

              {order.status === 'READY' && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${theme.stroke}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: theme.primary,
                      mr: 0.5,
                    }}
                  >
                    Click to view
                  </Typography>
                  <ChevronRightIcon
                    sx={{ fontSize: 18, color: theme.primary }}
                  />
                </Box>
              )}

              {order.status === 'PROCESSING' && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: theme.textLight,
                    mt: 1,
                    fontStyle: 'italic',
                  }}
                >
                  Your claim is being prepared. This usually takes a few hours.
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Submit Claim</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: theme.textLight, fontSize: 14 }}>
            An insurance claim will be submitted for this property. A claims
            processing professional will contact you with further information
            before we process this claim.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowConfirmModal(false)}
            sx={{ textTransform: 'none', color: theme.text }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmit}
            sx={{
              backgroundColor: `var(--fi-button, ${theme.primary})`,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: `var(--fi-button, ${theme.primary})`,
                opacity: 0.9,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealClaims;
